import { ProgressBar } from "@/components";
import { isWebview } from "@/libraries/helper";
import { AuthWebviewSchema } from "@/schemas";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import type { FileRoutesByPath } from "@tanstack/react-router";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getProfileLoader } from "../-functions/loader/profile";

const pathSteps = [
	"/create-account",
	"/create-account/verify-mobile",
	"/create-account/professional-credentials",
	"/create-account/professional-profile",
	"/create-account/cookie-credentials",
] as const;
const backPaths = [
	"/",
	"/create-account",
	"/create-account/verify-mobile",
	"/create-account/professional-credentials",
	"/create-account/professional-profile",
] as const;

export const Route = createFileRoute("/create-account")({
	validateSearch: zodSearchValidator(AuthWebviewSchema),
	loader: ({ context }) => getProfileLoader(context),
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation(["auth", "common"]);
	const labelPathSteps = t("auth:createAccount.steps", {
		returnObjects: true,
	}) as string[];
	const pathname = useLocation({
		select: (location) => location.pathname as keyof FileRoutesByPath,
	});
	const currentStep = useMemo(
		() => pathSteps.findIndex((step) => step.includes(pathname)),
		[pathname],
	);
	const hasWebview = isWebview();

	return (
		<div className="h-full flex flex-col overflow-clip">
			<AppBar
				position="relative"
				className="container !bg-white text-dark !shadow-none pb-2"
			>
				<Toolbar
					disableGutters
					variant="dense"
					className="w-full flex justify-between items-center mx-auto"
				>
					{!hasWebview && (
						<Link to={backPaths[currentStep]} className="flex items-center">
							<IconButton
								edge="start"
								color="inherit"
								aria-label="backspace"
								className="mr-2 text-2xl"
							>
								<KeyboardBackspaceIcon className="text-dark" />
							</IconButton>
							<p className="text-sm text-dark font-semibold">
								{t("common:back")}
							</p>
						</Link>
					)}
				</Toolbar>

				<Box>
					<ProgressBar
						min={0}
						max={5}
						value={currentStep + 1}
						step={currentStep + 1}
					/>
					<p className="text-tiny text-dark font-medium">
						{t("auth:createAccount.stepOf", {
							current: currentStep + 1,
							total: 5,
						})}
					</p>
					<p className="text-xs text-dark">{labelPathSteps[currentStep]}</p>
				</Box>
			</AppBar>
			<Outlet />
		</div>
	);
}
